import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { css } from '@emotion/react';
import { Box } from '@mui/material';

import LoaderQrIcon from '~/assets/icons/svgs/loader-qr.svg?react';
import previewBarBackground from '~/assets/images/iphone-white-bg.webp';
import previewExample from '~/assets/images/preview-example.png';
import CouponPreview from '~/components/builder/CouponPreview/CouponPreview';
import TestScan from '~/components/builder/TestScan';
import VideoPreview from '~/components/builder/VideoPreview/VideoPreview';
import { AppsPreviewScreen } from '~/components/organisms/previewBar/components/apps/AppsPreviewScreen';
import { ImagesPreviewScreen } from '~/components/organisms/previewBar/components/images/ImagesPreviewScreen';
import { InitialEmptyScreen } from '~/components/organisms/previewBar/components/InitialEmptyScreen';
import { ListOfLinksPreviewScreen } from '~/components/organisms/previewBar/components/list-of-links/ListOfLinksPreviewScreen';
import { PdfPreviewScreen } from '~/components/organisms/previewBar/components/pdf/PdfPreviewScreen';
import { SocialMediaPreviewScreen } from '~/components/organisms/previewBar/components/social-media/SocialMediaPreviewScreen';
import { VCardPlusPreviewScreen } from '~/components/organisms/previewBar/components/vcard-plus/VCardPlusPreviewScreen';
import { WebsitePreviewScreen } from '~/components/organisms/previewBar/components/website/WebsitePreviewScreen';
import { StyledTab, StyledTabs } from '~/components/ui/StyledTabs';
import { Text } from '~/components/ui/text';
import { PREVIEW_BAR_VIEW_TYPE } from '~/constants/common';
import {
  QR_CODE_ID_PARAM_NAME,
  QR_CODE_TEMPLATE_PARAM_NAME,
  QR_CODE_TYPE,
  QRCODE_DYNAMIC_MICROSITE_TYPE,
} from '~/constants/qr-code';
import useQrCodeData from '~/hooks/useQrCodeData';
import useBuilderStore from '~/store/useBuilderStore';
import { COLORS } from '~/styles/colors';
import { DynamicQrCodeData, StaticQrCodeData } from '~/types/qrCode';
import { getAreQRCodeStylesChanged } from '~/utils/builder';

type BuilderPreviewProps = {
  isDisabledQrCodePreview?: boolean;
  isUpdatingQrCodePending?: boolean;
};

const PreviewsMap = {
  [QRCODE_DYNAMIC_MICROSITE_TYPE.WEBSITE]: () => <WebsitePreviewScreen />,
  [QRCODE_DYNAMIC_MICROSITE_TYPE.PDF]: () => <PdfPreviewScreen />,
  [QRCODE_DYNAMIC_MICROSITE_TYPE.LINKS]: <ListOfLinksPreviewScreen />,
  [QRCODE_DYNAMIC_MICROSITE_TYPE.APP]: () => <AppsPreviewScreen />,
  [QRCODE_DYNAMIC_MICROSITE_TYPE.IMAGES]: () => <ImagesPreviewScreen />,
  [QRCODE_DYNAMIC_MICROSITE_TYPE.VCARD_PLUS]: () => <VCardPlusPreviewScreen />,
  [QRCODE_DYNAMIC_MICROSITE_TYPE.SOCIAL_MEDIA]: () => <SocialMediaPreviewScreen />,
  [QRCODE_DYNAMIC_MICROSITE_TYPE.COUPON]: () => <CouponPreview />,
  [QRCODE_DYNAMIC_MICROSITE_TYPE.VIDEO]: () => <VideoPreview />,
};

export default function DesktopBuilderPreviewV2({
  isDisabledQrCodePreview,
  isUpdatingQrCodePending,
}: BuilderPreviewProps) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const qrCodeId = searchParams.get(QR_CODE_ID_PARAM_NAME);

  const styles = useStyles();
  const { previewViewType, setPreviewViewType, activeStep } = useBuilderStore();
  const { data: qrCodeData, isLoading: isLoadingQrCodeData } = useQrCodeData<DynamicQrCodeData | StaticQrCodeData>({
    qrCodeId,
  });

  const [isQrImageLoaded, setIsQrImageLoaded] = useState(false);

  const areQRCodeStylesChanged = !!qrCodeData && getAreQRCodeStylesChanged(qrCodeData);
  const isQrExist = !!qrCodeData;
  const isFirstStep = activeStep === 0;
  const isQrCodeVisible = PREVIEW_BAR_VIEW_TYPE.QRCODE === previewViewType && !isFirstStep;
  const isDynamic = qrCodeData?.type === QR_CODE_TYPE.DYNAMIC;
  const templateId = searchParams.get(QR_CODE_TEMPLATE_PARAM_NAME);

  const renderDynamicPreview = () => {
    if (isFirstStep) {
      return InitialEmptyScreen;
    }

    return PreviewsMap[templateId]();
  };

  return (
    <Box sx={styles.container}>
      <Box sx={{ position: 'sticky', top: '10px' }}>
        <StyledTabs>
          <StyledTab
            onClick={() => setPreviewViewType(PREVIEW_BAR_VIEW_TYPE.PHONE)}
            $isActive={previewViewType === PREVIEW_BAR_VIEW_TYPE.PHONE}
          >
            {t('buttons:preview')}
          </StyledTab>
          <StyledTab
            disabled={!isQrExist || isDisabledQrCodePreview || isFirstStep}
            onClick={() => isQrExist && !isDisabledQrCodePreview && setPreviewViewType(PREVIEW_BAR_VIEW_TYPE.QRCODE)}
            $isActive={previewViewType === PREVIEW_BAR_VIEW_TYPE.QRCODE}
          >
            {t('buttons:qrCode')}
          </StyledTab>
        </StyledTabs>
        <img src={qrCodeData?.qrImage} alt="" style={{ display: 'none' }} onLoad={() => setIsQrImageLoaded(true)} />
        {isQrCodeVisible ? (
          <Box
            sx={{
              backgroundColor: COLORS.white,
              borderRadius: '20px',
              position: 'relative',
              padding: '28px',
              textAlign: 'center',
              boxShadow: '0 0 20px 0 rgba(0, 0, 0, 0.05)',
              minHeight: '282px',
            }}
          >
            {isLoadingQrCodeData || isUpdatingQrCodePending || !isQrImageLoaded ? (
              <LoaderQrIcon />
            ) : (
              <img src={qrCodeData?.qrImage} alt="qr code image" width="100%" />
            )}
          </Box>
        ) : (
          <>
            {isQrExist && !isFirstStep ? (
              <Box
                sx={{
                  backgroundImage: `url(${previewBarBackground})`,
                  width: '278px',
                  aspectRatio: '73 / 150',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {isDynamic ? renderDynamicPreview() : null}
              </Box>
            ) : (
              <img src={previewExample} alt="preview example" width={278} />
            )}
          </>
        )}
        <TestScan qrImage={qrCodeData?.qrImage} />
        {(isQrCodeVisible || (isQrExist && !isFirstStep)) && (
          <>
            {areQRCodeStylesChanged ? (
              <Box
                sx={{
                  border: `1px solid ${COLORS.darkRed}`,
                  marginTop: '12px',
                  padding: '16px',
                  borderRadius: '20px',
                  display: 'flex',
                  gap: '8px',
                  backgroundColor: COLORS.redBg,
                }}
              >
                ℹ️
                <Text variant="input15" sx={{ color: COLORS.grey400 }}>
                  <Text component="span" sx={{ color: COLORS.textMain, textTransform: 'uppercase' }}>
                    {t('general:important')}:{' '}
                  </Text>
                  {t('general:readabilityQr')}
                </Text>
              </Box>
            ) : (
              <Box
                sx={{
                  border: `1px solid ${COLORS.blue100}`,
                  marginTop: '12px',
                  padding: '16px',
                  borderRadius: '20px',
                  display: 'flex',
                  gap: '8px',
                  backgroundColor: COLORS.blue50,
                }}
              >
                ℹ️
                <Text variant="input15" sx={{ color: COLORS.grey400 }}>
                  <Text component="span" sx={{ color: COLORS.textMain, textTransform: 'uppercase' }}>
                    {t('general:important')}:{' '}
                  </Text>
                  {t('createQrStep1:rememberAboutPreview')}
                </Text>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

function useStyles() {
  return {
    container: css`
      position: relative;
      flex: 1;
      min-width: 278px;
      max-width: 278px;
    `,
  };
}
